/********************************/
/*       BASE LAYER CLASS       */
/********************************/

/**
 * Abstract base class for all rendering layers
 * Provides texture rendering and state management
 */
export class BaseLayer {
  constructor(width, height, density, autoResize = true) {
    this.width = width;
    this.height = height;
    this.density = density;
    this.autoResize = autoResize;

    // Animation state
    this.loopPercent = 0;
    this.rotationX = 0;
    this.rotationY = 0;

    // Create the rendering buffer (framebuffer only)
    this.buffer = createFramebuffer({ width: width, height: height, format: FLOAT, depth: false, stencil: false });
    this.buffer.autoSized(autoResize);

    // Font will be set in init() when available
    this.font = null;
  }

  /**
   * Initialize the layer (called once during setup)
   * Override this method in subclasses for layer-specific setup
   */
  setup() {
    // Override in subclass
  }

  /**
   * Draw the layer content (called every frame)
   * Override this method in subclasses for layer-specific drawing
   */
  draw() {
    // Override in subclass
  }

  /**
   * Initialize the layer's buffer and run setup
   * @param {p5.Font} font - The font to use for text rendering
   */
  init(font) {
    this.font = font;
    this.buffer.begin();
    if (this.font) {
      textFont(this.font);
    }
    this.setup();
    this.buffer.end();
  }

  /**
   * Update the layer's animation state
   * @param {Object} state - Animation state {loopPercent, rotationX, rotationY}
   */
  updateState(state) {
    this.loopPercent = state.loopPercent;
    this.rotationX = state.rotationX;
    this.rotationY = state.rotationY;
  }

  /**
   * Render the layer by calling its draw method inside the buffer context
   */
  render() {
    this.buffer.begin();
    this.draw();
    this.buffer.end();
  }

  /**
   * Get the rendered buffer
   */
  getBuffer() {
    return this.buffer;
  }

  /**
   * Resize the layer's buffer and re-run setup
   * @param {number} width - New width
   * @param {number} height - New height
   */
  resize(width, height) {
    if (!this.autoResize) {
      return; // Don't resize layers that have autoResize disabled
    }

    this.width = width;
    this.height = height;

    // Re-run setup with new dimensions
    this.buffer.begin();
    if (this.font) {
      textFont(this.font);
    }
    this.setup();
    this.buffer.end();
  }
}
